import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { 
  Mail, Send, Clock, MoreVertical, Trash2, Play, XCircle, Users, FileText, Edit
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import CampaignReportModal from "./CampaignReportModal";
import EditCampaignModal from "./EditCampaignModal";

export default function CampaignList({ onRefresh }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [reportCampaignId, setReportCampaignId] = useState(null);
  const [editCampaign, setEditCampaign] = useState(null);
  const inFlightRef = useRef(false);
  const lastStatusRef = useRef({});
  const pollRef = useRef(null);
  const [emailConfigs, setEmailConfigs] = useState([]);
  const [importedFiles, setImportedFiles] = useState([]);
  const [templates, setTemplates] = useState([]);

  const fetchCampaigns = useCallback(async (silent = false) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      if (!silent) {
        setLoading(true);
      }
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const [campaignRes, configsRes, filesRes, templatesRes] = await Promise.all([
        api.get(`/campaigns${params}`),
        api.get("/email-configs"),
        api.get("/vessels/imports"),
        api.get("/templates")
      ]);
      setCampaigns(campaignRes.data);
      setEmailConfigs(configsRes.data);
      setImportedFiles(filesRes.data);
      setTemplates(templatesRes.data);

      // Toast on status changes to failed
      const nextStatus = {};
      campaignRes.data.forEach((c) => {
        nextStatus[c.campaign_id] = {
          status: c.status,
          send_error: c.send_error || ""
        };
        const prev = lastStatusRef.current[c.campaign_id];
        if (prev && prev.status !== "failed" && c.status === "failed") {
          toast.error(c.send_error || "Campaign failed to send");
        }
      });
      lastStatusRef.current = nextStatus;
    } catch (error) {
      if (!silent) {
        toast.error("Failed to load campaigns");
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
      inFlightRef.current = false;
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const getFromEmail = (campaign) => {
    if (!campaign.email_config_id) return "Default";
    const config = emailConfigs.find((c) => c.config_id === campaign.email_config_id);
    return config?.from_email || "Unknown";
  };

  const getFileName = (campaign) => {
    if (!campaign.imported_file_id) return null;
    const file = importedFiles.find((f) => f.file_id === campaign.imported_file_id);
    return file?.file_name || "Imported file";
  };

  const getTemplateName = (campaign) => {
    if (!campaign.template_id) return null;
    const template = templates.find((t) => t.template_id === campaign.template_id);
    return template?.name || "Template";
  };

  // Poll only while campaigns are actively sending
  useEffect(() => {
    const shouldPoll = campaigns.some((c) => c.status === "sending");
    if (shouldPoll && !pollRef.current) {
      pollRef.current = setInterval(() => {
        fetchCampaigns(true);
      }, 15000);
    }
    if (!shouldPoll && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [campaigns, fetchCampaigns]);

  const [sendingId, setSendingId] = useState(null);

  const handleSend = async (campaignId) => {
    setSendingId(campaignId);
    try {
      await api.post(`/campaigns/${campaignId}/send`);
      toast.success("Campaign is being sent!");
      fetchCampaigns();
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send campaign");
    } finally {
      setSendingId(null);
    }
  };

  const handleRetryFailed = async (campaignId) => {
    setSendingId(campaignId);
    try {
      await api.post(`/campaigns/${campaignId}/retry-failed`);
      toast.success("Retrying failed recipients...");
      fetchCampaigns();
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to retry");
    } finally {
      setSendingId(null);
    }
  };

  const handleCancel = async (campaignId) => {
    try {
      await api.post(`/campaigns/${campaignId}/cancel`);
      toast.success("Campaign cancelled");
      fetchCampaigns();
    } catch (error) {
      toast.error("Failed to cancel campaign");
    }
  };

  const handleDelete = async (campaignId) => {
    try {
      await api.delete(`/campaigns/${campaignId}`);
      toast.success("Campaign deleted");
      fetchCampaigns();
      onRefresh?.();
    } catch (error) {
      toast.error("Failed to delete campaign");
    }
  };

  const handleDuplicate = async (campaignId) => {
    try {
      await api.post(`/campaigns/${campaignId}/duplicate`);
      toast.success("Campaign duplicated");
      fetchCampaigns();
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to duplicate campaign");
    }
  };

  const getStatusBadge = (status) => {
    const classes = {
      draft: "badge-draft",
      scheduled: "badge-scheduled",
      sending: "badge-sending",
      sent: "badge-sent",
      failed: "bg-destructive/10 text-destructive border border-destructive/20"
    };
    return <Badge className={classes[status] || "badge-draft"}>{status}</Badge>;
  };

  const stats = {
    total: campaigns.length,
    draft: campaigns.filter(c => c.status === "draft").length,
    scheduled: campaigns.filter(c => c.status === "scheduled").length,
    sending: campaigns.filter(c => c.status === "sending").length,
    sent: campaigns.filter(c => c.status === "sent").length,
    failed: campaigns.filter(c => c.status === "failed").length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total</div>
        </div>
        <div className="stat-card">
          <div className="stat-value text-muted-foreground">{stats.draft}</div>
          <div className="stat-label">Draft</div>
        </div>
        <div className="stat-card">
          <div className="stat-value text-chart-3">{stats.scheduled}</div>
          <div className="stat-label">Scheduled</div>
        </div>
        <div className="stat-card">
          <div className="stat-value text-blue-400">{stats.sending}</div>
          <div className="stat-label">Sending</div>
        </div>
        <div className="stat-card">
          <div className="stat-value text-chart-2">{stats.sent}</div>
          <div className="stat-label">Sent</div>
        </div>
        <div className="stat-card">
          <div className="stat-value text-destructive">{stats.failed}</div>
          <div className="stat-label">Failed</div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex justify-end">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="campaign-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Campaigns</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="sending">Sending</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Campaign List */}
      {campaigns.length === 0 ? (
        <div className="empty-state">
          <Mail className="empty-state-icon" />
          <h3 className="text-lg font-semibold mb-2">No campaigns found</h3>
          <p className="text-muted-foreground">
            Create your first email campaign to get started
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {campaigns.map((campaign) => (
            <div 
              key={campaign.campaign_id}
              className="bg-card border border-border rounded-lg p-5 card-hover"
              data-testid={`campaign-card-${campaign.campaign_id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate">{campaign.name}</h3>
                  <p className="text-sm text-muted-foreground truncate">
                    {campaign.subject}
                  </p>
                </div>
                {getStatusBadge(campaign.status)}
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="h-4 w-4" />
                  <span>{campaign.recipient_count} recipients</span>
                </div>

                <div className="text-sm text-muted-foreground">
                  <span className="font-medium text-muted-foreground">From:</span> {getFromEmail(campaign)}
                </div>

                {getFileName(campaign) && (
                  <div className="text-sm text-muted-foreground">
                    <span className="font-medium text-muted-foreground">File:</span> {getFileName(campaign)}
                  </div>
                )}

                {getTemplateName(campaign) && (
                  <div className="text-sm text-muted-foreground">
                    <span className="font-medium text-muted-foreground">Template:</span> {getTemplateName(campaign)}
                  </div>
                )}
                
                {campaign.scheduled_at && (
                  <div className="flex items-center gap-2 text-sm text-chart-3">
                    <Clock className="h-4 w-4" />
                    <span>{new Date(campaign.scheduled_at).toLocaleString()}</span>
                  </div>
                )}
                
                {campaign.sent_at && (
                  <div className="flex items-center gap-2 text-sm text-chart-2">
                    <Send className="h-4 w-4" />
                    <span>Sent {new Date(campaign.sent_at).toLocaleString()}</span>
                  </div>
                )}
                
                {(campaign.status === "sent" || campaign.status === "failed") && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-chart-2">{campaign.sent_count || 0} sent</span>
                    {campaign.failed_count > 0 && (
                      <span className="text-destructive">• {campaign.failed_count} failed</span>
                    )}
                  </div>
                )}
                
                {campaign.send_error && (
                  <div className="text-xs text-destructive mt-1 truncate" title={campaign.send_error}>
                    Error: {campaign.send_error}
                  </div>
                )}
                
                <div className="text-xs text-muted-foreground">
                  Created {new Date(campaign.created_at).toLocaleDateString()}
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-border">
                <div className="flex gap-2">
                  {(campaign.status === "draft" || campaign.status === "failed") && (
                    <Button
                      size="sm"
                      className="btn-primary"
                      onClick={() => handleSend(campaign.campaign_id)}
                      disabled={sendingId === campaign.campaign_id}
                      data-testid={`send-campaign-${campaign.campaign_id}`}
                    >
                      <Play className="h-3 w-3 mr-1" />
                      {sendingId === campaign.campaign_id ? "Sending..." : (campaign.status === "failed" ? "Retry" : "Send")}
                    </Button>
                  )}
                  
                  {campaign.status === "scheduled" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCancel(campaign.campaign_id)}
                    >
                      <XCircle className="h-3 w-3 mr-1" />
                      Cancel
                    </Button>
                  )}
                  
                  {(campaign.status === "sent" || campaign.status === "failed") && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setReportCampaignId(campaign.campaign_id)}
                      data-testid={`view-report-${campaign.campaign_id}`}
                    >
                      <FileText className="h-3 w-3 mr-1" />
                      View Report
                    </Button>
                  )}
                  {(campaign.status === "sent" || campaign.status === "failed") && (campaign.failed_count || 0) > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRetryFailed(campaign.campaign_id)}
                      disabled={sendingId === campaign.campaign_id}
                    >
                      {sendingId === campaign.campaign_id ? "Retrying..." : "Retry Failed"}
                    </Button>
                  )}
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {(campaign.status === "draft" || campaign.status === "scheduled" || campaign.status === "failed") && (
                      <DropdownMenuItem onClick={() => setEditCampaign(campaign)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => handleDuplicate(campaign.campaign_id)}>
                      <FileText className="h-4 w-4 mr-2" />
                      Duplicate
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleDelete(campaign.campaign_id)}
                      className="text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Campaign Report Modal */}
      {reportCampaignId && (
        <CampaignReportModal 
          campaignId={reportCampaignId} 
          onClose={() => setReportCampaignId(null)} 
        />
      )}

      {editCampaign && (
        <EditCampaignModal
          campaign={editCampaign}
          onClose={() => setEditCampaign(null)}
          onSuccess={() => {
            setEditCampaign(null);
            fetchCampaigns();
            onRefresh?.();
          }}
        />
      )}
    </div>
  );
}
